import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FlatList } from '@openx/components-core/lib/TransferList/FlatList';
import { ActionType } from '@openx/components-core/lib/TransferList/types';
import { Actions, LalSegmentFields, type LalSegmentFormState, type ListSegment } from '@openx/types/segments';
import { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mapListSegmentToSeedListData } from '../../../utils/mapListSegment';
import { SeedEstimatedReach } from '../SeedEstimatedReach/SeedEstimatedReach';
import { SeedListFlatList } from './SeedListFlatList';
import { SeedRenderItem } from './SeedRenderItem';
import { useHandleState } from './useHandleState';

const ITEM_SIZE = 120;

export const SeedListsAdd = ({
  segments,
  loadMore,
  hasMore,
  loadingMore,
  allItemsCount,
  onSearch,
}: {
  segments: ListSegment[];
  loadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  allItemsCount?: number;
  onSearch?: (searchTerm: string) => void;
}) => {
  const { t } = useTranslation();
  const { state, handleAddItem, handleRemoveAll, handleRemoveItem, dispatch } = useHandleState();
  const {
    formState: { errors },
  } = useFormContext<LalSegmentFormState>();

  const observableSeeds = useWatch({ name: LalSegmentFields.SEEDS });
  const listData = mapListSegmentToSeedListData(segments);

  useEffect(() => {
    dispatch({ type: Actions.SET_LIST_DATA, payload: observableSeeds || [] });
  }, [observableSeeds, dispatch]);

  const asyncSearchFn = useCallback(
    (searchInput: string) => {
      onSearch?.(searchInput);
      return Promise.resolve();
    },
    [onSearch]
  );

  return (
    <Grid
      container
      sx={{
        mt: 2,
        mb: 2,
      }}
    >
      <Grid size={6} data-test="segment-list">
        <Typography
          variant="body1"
          sx={{ mb: 1, fontWeight: 'fontWeightBold', fontSize: 15 }}
          data-test="segment-list-title"
        >
          {t('Segments List')}
        </Typography>
        <SeedListFlatList
          listData={listData.map(item => ({
            ...item,
            isOnList: observableSeeds?.some(seed => seed.id === item.id),
          }))}
          handleAddItem={handleAddItem}
          asyncSearchFn={asyncSearchFn}
          itemSize={ITEM_SIZE}
          loadMore={loadMore}
          hasMore={hasMore}
          loadingMore={loadingMore}
          allItemsCount={allItemsCount}
        />
      </Grid>

      <Grid size={6} data-test="applied-segments-list">
        <Typography
          variant="body1"
          sx={{ ml: 2, mb: 1, fontWeight: 'fontWeightBold', fontSize: 15 }}
          data-test="applied-segments-list-title"
        >
          {t('Assigned Segments')}
        </Typography>
        <FlatList
          dataTest="seed-remove-list"
          data={state.listData}
          handleItemRowClick={handleRemoveItem}
          isSelectedList
          handleRemoveAll={handleRemoveAll}
          actionType={ActionType.Add}
          isDisabled={false}
          hasBulkInput={false}
          errorMessage={errors[LalSegmentFields.SEEDS]?.message}
          fullWidth
          customItem={SeedRenderItem}
          customPlaceholder={t('Search Name')}
          itemSize={ITEM_SIZE}
          footer={<SeedEstimatedReach />}
        />
      </Grid>
    </Grid>
  );
};
